import { Modal, Box, Tab, Tabs, Button, Typography, CircularProgress } from "@mui/material";
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import { useDataProvider } from "react-admin";
import { TabPanel } from "../common/TabPanel";
import { FilePicker } from "./FilePicker";
import { FileUpload } from "./FileUpload";
import { hasExtension } from "./isImage";

export type FileInputModalProps = {
  multiple?: boolean;
  fileTypes?: string[];
  path?: string;
  paths?: string[];
  setPaths: any;
  includeDomain?: boolean;
  lockPath?: boolean;
};

export const FileInputModal = forwardRef((props: FileInputModalProps, ref) => {
  const { fileTypes, multiple, path, paths, setPaths, includeDomain, lockPath = false } = props;

  const [selected, setSelected] = useState([]);
  const [selectedDirectory, setSelectedDirectory] = useState(null);
  const [expandedDirectory, setExpandedDirectory] = useState([selectedDirectory]);
  const [data, setData] = useState([]);
  const [directories, setDirectories] = useState([]);
  const [tree, setTree] = useState([]);
  const [tabNum, setTabNum] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const filePickerRef = useRef<any>();
  
  const dataProvider = useDataProvider();

  const getItems = (pathItem?: string, treeDirectories?: any[]) => {
    setLoading(true);
    dataProvider.get(path, pathItem && { path: pathItem }).then(({ data }) => {
      setLoading(false);

      if (data.objects && paths && paths.length) {
        setSelected(data.objects.filter((i) => paths.includes(i.file)));
      }
      setData(data.objects);
      let mappedDirectories = createTree(data.directories, treeDirectories);

      if (null === selectedDirectory && data.directories.length > 0) {
        setSelectedDirectory(data.directories[0]);
      }

      if (data.objects.length === 0 && data.directories.length > 0) {
        setSelectedDirectory(data.directories[0]);

        if (pathItem !== data.directories[0]) {
          getItems(data.directories[0], mappedDirectories);
        }
      }
    });
  }

  const createTree = (items, treeDirectories: any[]) => {

    let stateDirectories = treeDirectories ?? directories;
    let directoryItems = [...new Set([...stateDirectories, ...items])];

    setDirectories(directoryItems);


    let directoryTree = [];
    let level = {directoryTree};

    directoryItems.forEach(pathItem => {
      pathItem.split('/').reduce((directory, name) => {
        if(!directory[name] && name !== "") {
          directory[name] = {directoryTree: []};
          directory.directoryTree.push({name, pathItem, children: directory[name].directoryTree})
        }
        
        return directory[name];
      }, level)
    });

    setTree(directoryTree);

    return directoryItems;
  };

  const handleRefresh = (path: string) => {
    setSelectedDirectory(path);
    setExpandedDirectory([path]);
    getItems();
  };

  const handleDirectorySelect = (event: React.SyntheticEvent|null, nodeId: string) => {
    if (null !== event && (event.target as Element).className !== "MuiTreeItem-label") {
      return;
    }
    setSelectedDirectory(nodeId);
    getItems(nodeId);
  };

  const handleDirectoryToggle = (event: React.SyntheticEvent|null, nodeIds: string[]) => {
    if (null !== event && (event.target as Element).className === "MuiTreeItem-label") {
      return;
    }
    setExpandedDirectory(nodeIds.filter(x => x != null));
  }

  useImperativeHandle(
    ref,
    () => {
      return {
        handleOpen() {
          setOpen(true);
          getItems();
        },
      };
    },
    []
  );

  const handleChangeTab = (event: React.SyntheticEvent, newTabNum: number) => {
    setTabNum(newTabNum);
  };

  const handleSave = () => {
    const currentRef = filePickerRef.current;
    if (currentRef) {
      setPaths(includeDomain ? currentRef.getSelectedPaths().map((pathItem) => `%cloudFrontUrl%${pathItem}`) : currentRef.getSelectedPaths());
    }
    setOpen(false);
  };

  const handleSingleInsert = (pathItem) => {
    setPaths([includeDomain ? `%cloudFrontUrl%${pathItem}` : pathItem]);
  };

  const handleSetSelected = (paths:string[] | undefined) => {
    setSelected(paths)
  }

  const handleSetLoading = (value: boolean) => {
    setLoading(value)
  }

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box sx={modalContentStyle}>
        <Box sx={tabBoxStyle}>
          <Tabs value={tabNum} onChange={handleChangeTab}>
            <Tab label="Pick from existing" />
            <Tab label="Upload new" />
          </Tabs>
          <TabPanel value={tabNum} index={0}>
            <FilePicker
              ref={filePickerRef}
              path={path}
              setSelected={handleSetSelected}
              handleDirectorySelect={handleDirectorySelect}
              handleDirectoryToggle={handleDirectoryToggle}
              selectedDirectory={selectedDirectory}
              expandedDirectory={expandedDirectory}
              setLoading={handleSetLoading}
              selected={selected}
              multiple={multiple}
              tree={tree}
              data={data}
            />
          </TabPanel>
          <TabPanel value={tabNum} index={1}>
            <FileUpload
              path={path}
              fileTypes={fileTypes}
              directories={directories}
              selectedDirectory={selectedDirectory}
              handleRefresh={handleRefresh}
              handleSingleInsert={handleSingleInsert}
              setLoading={setLoading}
              lockPath={lockPath}
            />
          </TabPanel>
        </Box>
        <Box sx={footerStyle}>
          { tabNum === 0 &&
            <Typography component="span" sx={{ fontSize: "16px", fontStyle: "italic", fontWeight: 600, flex: 1, paddingRight: "20px", alignSelf: "center"}}>{selected.map((i) => i.file).join(',')}</Typography>
          }
          { loading && <CircularProgress size={30} thickness={2} sx={{ fontSize: "28px", color: "#1976d2", marginRight: "8px", alignSelf: "center"}}/> }
          { tabNum === 0 &&
            <Button variant="contained" onClick={handleSave}>
              Insert
            </Button>
          }
          <Button variant="outlined" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
});

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  columnGap: "10px",
  paddingTop: "20px",
};

const tabBoxStyle = {
  width: "100%",
  height: "100%",
  minWidth: "550px",
  borderBottom: 1,
  borderColor: "divider",
};

const modalContentStyle = {
  position: "absolute" as "absolute",
  top: "25%",
  left: "50%",
  transform: "translate(-50%, -25%)",
  bgcolor: "white",
  boxShadow: 24,
  borderRadius: 1,
  p: 4,
};
